import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { FilterPipelineFilter } from 'record-it-components/hooks/useFilterPipeline/useFilterPipeline';
import { useSpecificParams } from 'record-it-components/hooks/useSpecificParams/useSpecificParams';
import {
  HeaderConfig,
  ManageEntitiesContentLayout
} from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { TimeTrackingActivityCompilation } from '../../../classes/entities/TimeTrackingActivityCompilation/timeTrackingActivityCompilationTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { createUserGroupIdUrlFilter } from '../../../filters/userGroupIdUrlFilter/userGroupIdUrlFilter';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { useEmptyCreateEntityButtonConfig } from '../../../hooks/useEmptyCreateEntityButtonConfig';
import { TimeTrackingActivityCompilationList } from '../../timeTrackingActivityCompilation/TimeTrackingActivityCompilationList/TimeTrackingActivityCompilationList';

export type TimeTrackingActivityCompilationAdminPageProps =
  PropsWithoutChildren<Record<never, never>>;

export const TimeTrackingActivityCompilationAdminPage: FunctionComponent<TimeTrackingActivityCompilationAdminPageProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    const { getRequiredCurrentUserPermissions } = useCurrentUser();
    const { t } = useTranslation();
    const { userGroupId } = useSpecificParams({
      requiredParamNames: ['userGroupId']
    });
    useConfigureBreadcrumbsWithBuilder((builder) =>
      builder
        .addHomePage()
        .addTimeTrackingActivityCompilationAdminPage(userGroupId)
    );

    const activities = useEntities(
      EntityName.TimeTrackingActivityCompilation,
      'getByUserGroupId',
      userGroupId
    );
    const repository = useEntityRepository(
      EntityName.TimeTrackingActivityCompilation
    );

    const handleCreateEntity = useEventCallback(() => {
      repository.create({
        name: '',
        ownerUserGroupId: userGroupId
      });
    });

    const createEntityButtonConfig = useEmptyCreateEntityButtonConfig({
      onCreateEntity: handleCreateEntity,
      text: t(
        'components.page.TimeTrackingActivityCompilationAdminPage.createEntity'
      )
    });

    const headerConfig: HeaderConfig = useMemo(() => {
      return {
        iconConfig:
          iconConfigurations.prefixed.page
            .TimeTrackingActivityCompilationAdminPage.titleIcon,
        title: t(
          'components.page.TimeTrackingActivityCompilationAdminPage.pageTitle'
        )
      };
    }, [t]);

    const filters: Array<
      FilterPipelineFilter<TimeTrackingActivityCompilation, any>
    > = useMemo(() => {
      const permissions = getRequiredCurrentUserPermissions();

      return [
        createUserGroupIdUrlFilter({
          userGroups:
            permissions.getEditableUserGroupsForTimeTrackingActivityCompilations(),
          currentUserGroupId: userGroupId,
          createUrl: (urlUserGroupId) =>
            RoutePaths.adminTimeTrackingActivityCompilations(urlUserGroupId)
        })
      ];
    }, [getRequiredCurrentUserPermissions, userGroupId]);

    const renderEntities = useCallback(
      (currentPageCompilations: Array<TimeTrackingActivityCompilation>) => {
        return (
          <TimeTrackingActivityCompilationList
            timeTrackingActivityCompilations={currentPageCompilations}
            data-testid="TimeTrackingActivityCompilationAdminPage.ActivityList"
          ></TimeTrackingActivityCompilationList>
        );
      },
      []
    );

    return (
      <ManageEntitiesContentLayout<TimeTrackingActivityCompilation, never>
        createEntityButtonConfig={createEntityButtonConfig}
        headerConfig={headerConfig}
        entities={activities}
        renderEntities={renderEntities}
        usePagination={false}
        filters={filters}
      ></ManageEntitiesContentLayout>
    );
  });
